// Fuente ÚNICA de la lista de compatibilidad de shaders con los pasos del Mod Cofrade.
//
// Vive en Firestore (`shader_compat/main`), editable por el admin desde
// /jartinet/admin/shaders. La constante DEFAULT_SHADERS es solo el semilla/reserva: si el
// documento aún no existe, la página usa esta lista; en cuanto el admin guarda, Firestore manda.

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';

export type ShaderRow = {
  key: string;   // fragmento que aparece en el nombre del archivo (minúsculas, sin símbolos)
  name: string;
  ok: boolean;   // true = el movimiento del palio se ve bien
  ver: string;
};

/** Semilla = lo probado en juego hasta 2026-07. */
export const DEFAULT_SHADERS: ShaderRow[] = [
  { key: 'photon', name: 'Photon', ok: true, ver: 'v1.0a' },
  { key: 'makeup', name: 'MakeUp UltraFast', ok: true, ver: '8.8c' },
  { key: 'bsl', name: 'BSL', ok: false, ver: 'v8.2.02' },
  { key: 'complementary', name: 'Complementary', ok: false, ver: 'v4.7.2' },
  { key: 'chocapic', name: 'Chocapic13', ok: false, ver: 'V9 Ultra' },
  { key: 'kappa', name: 'Kappa', ok: false, ver: 'v5.3' },
  { key: 'seus', name: 'SEUS Renewed', ok: false, ver: 'v1.0.1' },
  { key: 'sildur', name: "Sildur's Vibrant", ok: false, ver: 'v1.50' },
  { key: 'solas', name: 'Solas', ok: false, ver: 'V1.5' },
  { key: 'voyager', name: 'Voyager', ok: false, ver: '2.0.8' },
  { key: 'bloop', name: 'Bloop', ok: false, ver: '1.4.2' },
  { key: 'exposa', name: 'Exposa', ok: false, ver: '4.13' },
  { key: 'vhs', name: 'MC VHS', ok: false, ver: '0.6' },
  { key: 'napshader', name: 'Nap Shaders', ok: false, ver: 'old' },
  { key: 'lite', name: 'LITE Shaders', ok: false, ver: '4.7.3' },
];

export const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Lee la lista de Firestore; si el documento no existe todavía, devuelve la semilla. */
export async function getShaderCompat(): Promise<ShaderRow[]> {
  try {
    const snap = await getDoc(doc(db, 'shader_compat', 'main'));
    const rows = snap.exists() ? (snap.data()?.shaders as ShaderRow[] | undefined) : undefined;
    if (Array.isArray(rows) && rows.length) {
      return rows
        .filter((r) => r && r.key && r.name)
        .map((r) => ({ key: norm(r.key), name: String(r.name), ok: r.ok === true, ver: String(r.ver || '') }));
    }
  } catch { /* offline / permiso: cae a la semilla */ }
  return DEFAULT_SHADERS;
}

/** Guarda la lista (solo admin, exigido por las reglas de Firestore). */
export async function setShaderCompat(shaders: ShaderRow[]) {
  const clean = shaders
    .map((r) => ({ key: norm(r.key), name: String(r.name || '').trim(), ok: r.ok === true, ver: String(r.ver || '').trim() }))
    .filter((r) => r.key && r.name);
  try {
    await setDoc(doc(db, 'shader_compat', 'main'), { shaders: clean, updatedAt: serverTimestamp() });
    return { success: true, message: 'Lista guardada.' };
  } catch (e: any) {
    return { success: false, message: `No se pudo guardar: ${e.message}` };
  }
}
